import { css } from '@emotion/react';
import { Typography } from '@mui/material';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { useDraggable } from '@dnd-kit/core';
import { colors } from '../../../../../theme/colors';
import { PanelMetadata, TearsheetPanelType } from '../types';

const panelCardStyle = css`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem;
  background: white;
  border: 1px solid ${colors.neutral[20]};
  border-radius: 6px;
  cursor: grab;
  transition: all 0.15s ease;
  user-select: none;
  touch-action: none;

  &:hover {
    border-color: ${colors.primary[30]};
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  }

  &:active {
    cursor: grabbing;
  }
`;

const panelCardDraggingStyle = css`
  opacity: 0.5;
  border-color: ${colors.primary[40]};
`;

const panelCardDisabledStyle = css`
  opacity: 0.5;
  cursor: not-allowed;
  background: ${colors.neutral[5]};

  &:hover {
    border-color: ${colors.neutral[20]};
    box-shadow: none;
  }
`;

const dragHandleStyle = css`
  color: ${colors.neutral[40]};
  flex-shrink: 0;
`;

const panelInfoStyle = css`
  flex: 1;
  min-width: 0;
  overflow: hidden;
`;

const descriptionStyle = css`
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export interface DraggablePanelData {
  panelType: TearsheetPanelType;
  source: 'palette';
}

interface PanelCardProps {
  panel: PanelMetadata;
  isUsed: boolean;
}

export function PanelCard({ panel, isUsed }: PanelCardProps) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `palette-${panel.type}`,
    data: {
      panelType: panel.type,
      source: 'palette',
    } as DraggablePanelData,
    disabled: isUsed,
  });

  return (
    <div
      ref={setNodeRef}
      css={[panelCardStyle, isDragging && panelCardDraggingStyle, isUsed && panelCardDisabledStyle]}
      {...listeners}
      {...attributes}
    >
      <DragIndicatorIcon css={dragHandleStyle} fontSize='small' />
      <div css={panelInfoStyle}>
        <Typography variant='body2' fontWeight={500} noWrap>
          {panel.label}
        </Typography>
        <Typography variant='caption' color='text.secondary' css={descriptionStyle}>
          {panel.description}
        </Typography>
      </div>
    </div>
  );
}
